import React, { useState, useEffect } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { io } from 'socket.io-client';

interface Props { user: any; onLogout: () => void; }

const NAV_SECTIONS = [
  {
    label: 'PRINCIPAL',
    items: [
      { path: '/', icon: '📊', label: 'Dashboard', end: true },
      { path: '/employees', icon: '👥', label: 'Empleados' },
      { path: '/teams', icon: '🏆', label: 'Equipos' },
    ]
  },
  {
    label: 'ACTIVIDAD',
    items: [
      { path: '/screenshots', icon: '🖼️', label: 'Capturas' },
      { path: '/apps', icon: '💻', label: 'Aplicaciones' },
      { path: '/webs', icon: '🌐', label: 'Navegación web' },
    ]
  },
  {
    label: 'ANÁLISIS',
    items: [
      { path: '/reports', icon: '📋', label: 'Informes' },
      { path: '/ai', icon: '✨', label: 'IA' },
    ]
  },
  {
    label: 'SISTEMA',
    items: [
      { path: '/remote', icon: '🖥️', label: 'Escritorio remoto' },
      { path: '/audit', icon: '🔍', label: 'Auditoría' },
      { path: '/companies', icon: '🏢', label: 'Empresa' },
      { path: '/settings', icon: '⚙️', label: 'Configuración' },
    ]
  },
];

export function Layout({ user, onLogout }: Props) {
  const [collapsed, setCollapsed] = useState(false);
  const [liveCount, setLiveCount] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('at_token');
    const socket = io('/track', { auth: { token }, transports: ['websocket'] });
    socket.on('user:clock_in', () => setLiveCount(c => c + 1));
    socket.on('user:clock_out', () => setLiveCount(c => Math.max(0, c - 1)));
    return () => { socket.disconnect(); };
  }, []);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Sidebar */}
      <aside style={{
        width: collapsed ? 56 : 220,
        background: 'var(--bg2)',
        borderRight: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s',
        flexShrink: 0,
        overflow: 'hidden',
      }}>
        {/* Logo */}
        <div style={{ padding: '14px 12px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18, flexShrink: 0 }}>⚡</span>
          {!collapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13, letterSpacing: -0.3 }}>AccioTrack</div>
              <div style={{ fontSize: 9, color: 'var(--text3)' }}>AccioSoft</div>
            </div>
          )}
          <button onClick={() => setCollapsed(!collapsed)} style={{ background: 'transparent', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: 4, borderRadius: 4, fontSize: 10, flexShrink: 0 }}>
            {collapsed ? '→' : '←'}
          </button>
        </div>

        {/* Live indicator */}
        {!collapsed && liveCount > 0 && (
          <div style={{ margin: '8px 10px 0', padding: '5px 10px', background: 'var(--green-dim)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--green)' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)', boxShadow: '0 0 6px var(--green)', display: 'inline-block' }} />
            {liveCount} activo{liveCount !== 1 ? 's' : ''}
          </div>
        )}

        {/* Nav */}
        <nav style={{ flex: 1, padding: '8px 6px', display: 'flex', flexDirection: 'column', gap: 0, overflowY: 'auto' }}>
          {NAV_SECTIONS.map(section => (
            <div key={section.label} style={{ marginBottom: 4 }}>
              {!collapsed && (
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text3)', letterSpacing: '0.1em', padding: '8px 8px 4px', textTransform: 'uppercase' }}>
                  {section.label}
                </div>
              )}
              {section.items.map(item => (
                <NavLink key={item.path} to={item.path} end={item.end}
                  style={({ isActive }) => ({
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: collapsed ? '8px 10px' : '7px 8px',
                    borderRadius: 8, fontSize: 12,
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? 'var(--accent)' : 'var(--text2)',
                    background: isActive ? 'var(--accent-dim)' : 'transparent',
                    transition: 'all 0.1s', textDecoration: 'none',
                    whiteSpace: 'nowrap', overflow: 'hidden',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                  })}>
                  <span style={{ fontSize: 14, flexShrink: 0 }}>{item.icon}</span>
                  {!collapsed && item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* User */}
        <div style={{ padding: '10px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            background: 'var(--accent-dim)', color: 'var(--accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, flexShrink: 0,
          }}>
            {user?.name?.charAt(0)?.toUpperCase()}
          </div>
          {!collapsed && (
            <>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
                <div style={{ fontSize: 9, color: 'var(--text3)', textTransform: 'capitalize' }}>{user?.role}</div>
              </div>
              <button onClick={onLogout} style={{ background: 'transparent', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: 4, borderRadius: 4, fontSize: 12 }} title="Cerrar sesión">🚪</button>
            </>
          )}
        </div>
      </aside>

      {/* Main */}
      <main style={{ flex: 1, overflow: 'auto', background: 'var(--bg)' }}>
        <Outlet />
      </main>
    </div>
  );
}
